import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserDto } from '@bo-client/shared/models/userDto';
import { path } from '@bo-client/shared/constants/path';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { UserRole } from '@bo-client/shared/models/user-roles.enum';

@Injectable()
export class AuthorizationService {
  private userData: UserDto;

  constructor(
    private http: HttpClient,
    private httpHapService: HttpHapService,
    private dataShareService: DataShareService
  ) {
    dataShareService.userData$.subscribe((x) => {
      this.userData = x;
    });
  }

  hasRole(role: UserRole): boolean {
    try {
      if (this.dataShareService.userData$) {
        if (this.userData.roles.length > 0) {
          for (let i = 0; i < this.userData.roles.length; i++) {
            if (this.userData.roles[i].name == role) {
              return true;
            }
          }
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  hasRole$(role: UserRole): Observable<boolean> {
    return this.dataShareService.userData$.pipe(
      filter((userData) => !!userData),
      map((userData) => {
        if (!userData || userData.roles.length === 0) {
          return false;
        }

        return userData.roles.some((userRole) => userRole.name === role);
      }),
      catchError((error) => {
        // Fehlerbehandlung, ggf. Logging
        console.error('Fehler bei der Rollenüberprüfung', error);
        return of(false);
      })
    );
  }

  get isInstaller$(): Observable<boolean> {
    return this.hasOneOfThisRoles$(['ROLE_INSTALLATEUR', 'ROLE_ADMIN_INSTALLATEUR']);
  }

  get isEditor$(): Observable<boolean> {
    return this.hasRole$('ROLE_EDITOR_BASE');
  }

  canManageOtherUsersByAreas(): boolean {
    try {
      return this.userData.areaTypes.length > 0;
    } catch (error) {
      return false;
    }
  }

  hasPermission(permission: string) {
    if (!this.userData) {
      return false;
    }

    const userPermissions = this.userData.roles
      .reduce((prev, curren) => [...prev, ...curren.privileges], [])
      .map((x) => x.name);

    return userPermissions.includes(permission);
  }

  get getCurrentUser(): UserDto {
    return this.userData;
  }

  public hasPermission$(permission: string): Observable<boolean> {
    return this.dataShareService.userData$.pipe(
      map((userData) => {
        if (!userData) {
          return false;
        }

        const userPermissions = userData.roles
          .reduce((prev, current) => [...prev, ...current.privileges], [])
          .map((privilege) => privilege.name);

        return userPermissions.includes(permission);
      }),
      catchError((error) => {
        // Fehlerbehandlung, ggf. Logging
        console.error('Fehler bei der Berechtigungsprüfung', error);
        return of(false);
      })
    );
  }

  hasOneOfThisRoles(roles: UserRole[]): boolean {
    if (roles) {
      if (roles.length > 0) {
        for (let i = 0; i < roles.length; i++) {
          if (roles[i] == 'ALL_USERS') {
            return true;
          }
          if (this.hasRole(roles[i])) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Überprüft, ob der aktuelle Benutzer mindestens eine der angegebenen Rollen besitzt.
   *
   * Diese Methode subscribed das `userData$` Observable aus dem `DataShareService` und
   * überprüft, ob der aktuelle Benutzer eine der Rollen im `roles`-Array besitzt.
   * Wenn die Rolle 'ALL_USERS' im Parameter-Array enthalten ist, gibt die Methode `true` zurück.
   *
   * @param {UserRole[]} roles - Ein Array mit Rollennamen, die mit den Rollen des Benutzers verglichen werden sollen.
   * @returns {Observable<boolean>} Ein Observable, das `true` ausgibt, wenn der Benutzer mindestens eine der angegebenen Rollen besitzt, ansonsten `false`.
   */
  hasOneOfThisRoles$(roles: UserRole[]): Observable<boolean> {
    return this.dataShareService.userData$.pipe(
      filter((userData) => !!userData),
      map((userData) => {
        if (!userData || !userData.roles || userData.roles.length === 0) {
          return false;
        }

        for (let i = 0; i < roles.length; i++) {
          if (roles[i] == 'ALL_USERS') {
            return true;
          }
          if (userData.roles.some((userRole) => userRole.name === roles[i])) {
            return true;
          }
        }
        return false;
      }),
      catchError((error) => {
        // Fehlerbehandlung, ggf. Logging
        console.error('Fehler bei der Rollenüberprüfung', error);
        return of(false);
      })
    );
  }

  selectPrefixUrl(): string {
    let prefixUrl = path.tmPrefix;
    if (this.userData) {
      if (
        this.hasOneOfThisRoles([
          'ROLE_ADMIN_INSTALLATEUR',
          'ROLE_INSTALLATEUR',
          'ROLE_PENDING_CONFIRM',
          'ROLE_PENDING_VERIFY'
        ])
      ) {
        prefixUrl = path.tmPath;
      } else {
        prefixUrl = '';
      }
    }
    return prefixUrl;
  }

  getLogoutRoute(): string {
    let route = path.home;
    if (
      this.userData &&
      this.hasOneOfThisRoles([
        'ROLE_ADMIN_INSTALLATEUR',
        'ROLE_INSTALLATEUR',
        'ROLE_PENDING_CONFIRM',
        'ROLE_PENDING_VERIFY'
      ])
    ) {
      route = path.tmPrefix + '/' + path.tmHome;
    }

    return route;
  }

  selectRouteLogin(): string {
    let loginURL = path.tmPath + '/home';
    if (this.dataShareService.currentUserConfiguration) {
      if (
        this.hasOneOfThisRoles([
          'ROLE_ADMIN_INSTALLATEUR',
          'ROLE_INSTALLATEUR',
          'ROLE_PENDING_CONFIRM',
          'ROLE_PENDING_VERIFY'
        ])
      ) {
        loginURL = path.tmPath + path.tmDesktop;
      } else {
        loginURL = path.hapPrefix + path.hapHome;
      }
    }
    return loginURL;
  }
}
